import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import copy from 'copy-to-clipboard';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Text, TextProps } from '@lifeomic/chroma-react/components/Text';
import { Copy } from '@lifeomic/chromicons';

import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { Member } from './utils';

const messages = defineMessages({
  preferredName: {
    id: 'IndividualOnboardingView.preferredName',
    defaultMessage: '{preferredName}',
  },
  emailLabel: {
    id: 'IndividualOnboardingView.emailLabel',
    defaultMessage: 'Email',
  },
  email: {
    id: 'IndividualOnboardingView.email',
    defaultMessage: '{email}',
  },
  phoneLabel: {
    id: 'IndividualOnboardingView.phoneLabel',
    defaultMessage: 'Phone',
  },
  phone: {
    id: 'IndividualOnboardingView.phone',
    defaultMessage: '{phone}',
  },
  genderLabel: {
    id: 'IndividualOnboardingView.genderLabel',
    defaultMessage: 'Gender',
  },
  gender: {
    id: 'IndividualOnboardingView.gender',
    defaultMessage: '{gender}',
  },
  elationId: {
    id: 'IndividualOnboardingView.elationId',
    defaultMessage: 'Elation ID',
  },
  elationStatus: {
    id: 'IndividualOnboardingView.elationStatus',
    defaultMessage: 'Elation Status',
  },
  copyToClipboard: {
    id: 'IndividualOnboardingView.copyToClipboard',
    defaultMessage: 'Copy to clipboard',
  },
  dob: {
    id: 'IndividualOnboardingView.dob',
    defaultMessage: 'Date of Birth',
  },
  centerLabel: {
    id: 'IndividualOnboardingView.centerLabel',
    defaultMessage: 'Center',
  },
});

const useStyles = makeStyles(theme => ({
  patientInfo: {
    display: 'grid',
    gap: `0 ${theme.spacing(1.5)}`,
    gridTemplateColumns: '175px 1fr',
  },
  label: {
    color: theme.palette.text.hint,
    fontWeight: theme.typography.fontWeightBold,
  },
  divider: {
    gridColumn: '-1 / 1',
  },
  copyButtonContainer: {
    '& button': {
      opacity: 0,
    },
    '&:hover button': {
      opacity: 1,
    },
    transition: 'opacity 0.3s ease-in-out',
  },
  copyButton: {
    padding: 0,
  },
}));

const CopyableText: React.FC<
  Omit<TextProps, 'children'> & {
    value: string | undefined;
    linkTo?: string;
  }
> = ({ value: _value, linkTo, ...textProps }) => {
  const { formatMessage } = useIntl();
  const styles = useStyles();
  const value = _value || '';

  return (
    <Box
      direction="row"
      justify="space-between"
      align="center"
      className={styles.copyButtonContainer}
    >
      {linkTo ? (
        <a target="_blank" rel="noreferrer" href={linkTo}>
          <Text {...textProps}>{value}</Text>
        </a>
      ) : (
        <Text {...textProps}>{value}</Text>
      )}
      <IconButton
        icon={Copy}
        onClick={() => copy(value)}
        aria-label={formatMessage(messages.copyToClipboard)}
        disabled={!value}
        size={0}
        className={styles.copyButton}
      />
    </Box>
  );
};

export const MemberInfo: React.FC<{ member: Member }> = props => {
  const styles = useStyles();
  const { formatMessage } = useIntl();

  return (
    <>
      {props.member.preferredName ? (
        <Text
          onClick={() => {
            props.member.preferredName && copy(props.member.preferredName);
          }}
        >
          {formatMessage(messages.preferredName, {
            preferredName: props.member.preferredName,
          })}
        </Text>
      ) : null}
      <CopyableText size="headline" weight="bold" value={props.member.name} />
      <Box className={styles.patientInfo}>
        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.elationId)}
        </Text>
        <CopyableText
          size="subbody"
          value={props.member.elationId}
          linkTo={props.member.elationChartUrl}
        />

        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.elationStatus)}
        </Text>
        <Text size="subbody">{props.member.elationStatus}</Text>

        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.dob)}
        </Text>
        <Text size="subbody">{props.member.dateOfBirth}</Text>

        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.emailLabel)}
        </Text>
        <CopyableText size="subbody" value={props.member.email} />
        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.phoneLabel)}
        </Text>
        <CopyableText size="subbody" value={props.member.phone} />
        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.genderLabel)}
        </Text>
        <Text size="subbody">
          {formatMessage(messages.gender, {
            gender: props.member.gender,
          })}
        </Text>
        <Divider className={styles.divider} />
        <Text className={styles.label} size="subbody">
          {formatMessage(messages.centerLabel)}
        </Text>
        <Text size="subbody">{props.member.centerName}</Text>
        <Divider className={styles.divider} />
      </Box>
    </>
  );
};
